/**
 * **出牌动画** —— 打出的牌从出牌处飞到画面正中，停 `PLAY_HOLD_MS`，然后按
 * 打断链的奇偶决定"生效 → 飞进弃牌堆"还是"被取消 → 淡出"。
 *
 * 计划文档 \`docs/interrupt-rework-plan.md\` §5 / §11.2 给的动作：
 *
 * > 牌面从出牌处飞到画面中心 → 停 `PLAY_HOLD_MS = 500ms` → 生效 → 飞入弃牌堆。
 *
 * 和抽牌动画（`drawReveal.ts`）的分工：
 *
 * | | 抽牌动画 | 出牌动画 |
 * | --- | --- | --- |
 * | 触发 | `card/drawn` 事件 | 视图里出现一个新的 `resolution` |
 * | 起手 | **牌背**，飞的过程中翻面 | **牌面**（手里就看得见，见 `CardFlip.faceUp`） |
 * | 中途 | 停一下看清抽到的是什么 | 停 `PLAY_HOLD_MS`，让人有机会打断 |
 * | 落点 | 手牌留出的那一格 | 弃牌堆顶 |
 * | 取消 | 不适用 | 打断链奇数 → 淡出，不飞弃牌堆 |
 *
 * ## 时序是**纯函数**，不是藏在 `update` 里的 if
 *
 * 相位时长（`playPhaseMs`）和"停完之后去哪"（`playSettlePhase`）都抽成
 * 纯函数 —— 它们是"画面和状态一致"的唯一依据，所以能在 node 里钉住，
 * 而不是只能靠肉眼盯着屏幕数五百毫秒。
 *
 * ## ⚠️ 纯视觉，绝不写回状态
 *
 * 这一层只读视图、只画。牌什么时候进弃牌堆由**房主**的 reducer 决定 ——
 * 客户端这里飞的只是"状态即将变成那样"的画面。取消与否在读的时候问一次
 * `resolution.nopeChain.length`，从不反过来改它。
 */

import { Container, type Renderer, type Ticker } from 'pixi.js';
import type { CardDef } from '../../core/card';
import type { CardFaceCache } from '../card';
import { CardFlip } from './cardFlip';
import { easeOut } from '../ui/motion';
import { FADE_MS, travelMs, type Pt } from './drawReveal';
import { GlitchVanishFilter } from './glitchVanish';

/**
 * **出牌动画在画面中心停多久（毫秒）。**
 *
 * ⚠️ 它和房主 `src/host/rules.ts` 里那个 `PLAY_HOLD_MS` 是**同一个数**：
 *    房主用它规划"什么时候关窗让效果生效"，客户端用同一段时长等人打断。
 *    两边漂开的话，画面会在状态生效之后还在停、或者反过来。
 *    `test/render/playReveal.test.ts` 把两个常量钉成相等。
 *
 * ⚠️ 改这里之前先读 `docs/interrupt-rework-plan.md` §11.2：
 *    打断窗口 = 这个数 + 每张断点 1s（上限 3s）。
 */
export const PLAY_HOLD_MS = 500;

/**
 * **每张 [断点] 给停顿延长多少（毫秒）。** 和房主 `NOPE_EXTENSION_MS` 是同一个数。
 *
 * 用户（计划 §11.2）：「每打出一张 [断点] → 当前 pending play 的 `nopeCount += 1`，
 * 并把超时 **+1s**（总延长上限 **3s**）。」画面必须跟着一起延长 —— 否则房主
 * 还在等打断，客户端已经把牌飞进弃牌堆了。
 */
export const NOPE_EXTENSION_MS = 1000;
/** 最多延长几张 [断点]（总延长上限 3s）。和房主的 `MAX_NOPE_EXTENSION` 同值 */
export const MAX_NOPE_EXTENSION = 3;

/**
 * **停顿该有多长。** 纯函数：基础 `PLAY_HOLD_MS` + `min(nopeCount, 3) × 1s`。
 *
 * ⚠️ 这是**最小停顿**。真正开窗多久由房主决定（提交后还多 0.5s 宽限），
 *    所以组件还要等 `setWindowOpen(false)`（房主关窗）才真正结算，见 `update`。
 */
export function playHoldMs(nopeCount: number): number {
  const n = Number.isFinite(nopeCount) ? Math.max(0, Math.floor(nopeCount)) : 0;
  return PLAY_HOLD_MS + Math.min(n, MAX_NOPE_EXTENSION) * NOPE_EXTENSION_MS;
}

/**
 * 出牌动画的相位。`glitch` 与 `fade` 二选一，取决于打断链的奇偶。
 *
 * ⚠️ 弃牌堆 UI 已删除（用户要求，记牌器取代了它的信息功能）—— 牌生效后
 * 不再"飞进弃牌堆"，而是**原地故障消失**（`glitch`）。
 */
export type PlayPhase = 'fly' | 'hold' | 'glitch' | 'fade' | 'done';

/** 生效后"故障消失"这一段多长（毫秒） */
export const GLITCH_VANISH_MS = 460;

/**
 * **停完之后去哪。** 纯函数：打断链奇数（`cancelled`）→ 淡出，
 * 否则**故障消失**（`glitch`）。
 *
 * 评审：「结算按 `nopeCount` 的奇偶：奇 → 取消效果；偶 → 生效。」
 */
export function playSettlePhase(cancelled: boolean): 'glitch' | 'fade' {
  return cancelled ? 'fade' : 'glitch';
}

/**
 * **一次待结算出牌的签名。** 纯函数，用来发现"出现了一个新的出牌"。
 *
 * 每次 `resolution` 从无到有、或者打出的牌换了，都要重新摆一次动画；
 * 而打断链增长时 `play` 一个字没变，签名也不变 —— 所以加断点不会
 * 把动画重新播一遍。`null` 表示现在没有待结算出牌。
 */
export function playSignature(
  play: { readonly player: string; readonly cards: readonly { readonly uid: number }[] } | null,
): string | null {
  if (play === null) return null;
  return `${play.player}:${play.cards.map((card) => card.uid).join(',')}`;
}

/** 每个相位多长（毫秒）。纯函数，测试直接钉它 */
export function playPhaseMs(phase: PlayPhase, from: Pt, center: Pt): number {
  switch (phase) {
    case 'fly':
      return travelMs(from, center);
    case 'hold':
      return PLAY_HOLD_MS;
    case 'glitch':
      return GLITCH_VANISH_MS;
    case 'fade':
      return FADE_MS;
    case 'done':
      return 0;
  }
}

export interface PlayRevealOptions {
  readonly renderer: Renderer;
  readonly def: CardDef;
  /** 牌**高**（逻辑像素） */
  readonly height: number;
  /** 复用手牌那份牌面缓存。不给就自己烘一张 */
  readonly faces?: CardFaceCache;
  /** 起飞点：出牌处（牌心）。actor 的牌从手牌那一带起飞 */
  readonly from: Pt;
  /** 中点：画面正中 */
  readonly center: Pt;
}

export class PlayReveal {
  /** 加进特效层的东西 */
  readonly view: Container;

  readonly #flip: CardFlip;
  readonly #from: Pt;
  readonly #center: Pt;
  readonly #half: { readonly w: number; readonly h: number };
  /** 生效后原地故障消失用的滤镜。进入 `glitch` 相位时才建（它要 WebGL） */
  #glitch: GlitchVanishFilter | null = null;

  #phase: PlayPhase = 'fly';
  #elapsed = 0;
  #phaseMs = 0;
  /** 打断链奇数 = 这次效果会被取消。`setCancelled` 在停顿结束前都能改它 */
  #cancelled = false;
  /** 打断链长度（每张 [断点] +1）。停顿期间喂进来会实时延长 */
  #nopeCount = 0;
  /** 房主那个出牌窗口还开着吗。见 `setWindowOpen` */
  #windowOpen = true;

  constructor(options: PlayRevealOptions) {
    this.#from = options.from;
    this.#center = options.center;
    /**
     * ★ **牌面** —— 出牌的牌在手里就看得见，不再翻面（`faceUp`）。
     */
    this.#flip = new CardFlip({
      renderer: options.renderer,
      def: options.def,
      height: options.height,
      ...(options.faces === undefined ? {} : { faces: options.faces }),
      faceUp: true,
    });

    this.view = new Container();
    this.view.label = 'play-reveal';
    this.view.eventMode = 'none';
    this.view.addChild(this.#flip.view);

    const size = this.#flip.size;
    this.#half = { w: size.width / 2, h: size.height / 2 };
    // ⚠️ 支点挪到**牌心**：位置 / 旋转都绕中心发生
    this.view.pivot.set(this.#half.w, this.#half.h);

    this.#phaseMs = playPhaseMs('fly', this.#from, this.#center);
    this.#place(this.#from, 1);
  }

  get phase(): PlayPhase {
    return this.#phase;
  }

  /** 当前这一段的时长（毫秒）。调参时最想看的那个数 */
  get phaseMs(): number {
    return this.#phaseMs;
  }

  get elapsed(): number {
    return this.#elapsed;
  }

  /** 现在这次出牌会不会被取消（打断链奇偶） */
  get cancelled(): boolean {
    return this.#cancelled;
  }

  get finished(): boolean {
    return this.#phase === 'done';
  }

  /**
   * **这次出牌被取消了吗。** 每帧由调用方按最新的视图喂进来。
   *
   * ⚠️ 只在停顿（以及飞行）期间有效：一旦已经滑进 `discard` / `fade`，
   *    结果就定了 —— 迟到的打断不再让一张已经飞走的牌倒回来。
   */
  setCancelled(cancelled: boolean): void {
    if (this.#phase === 'fly' || this.#phase === 'hold') this.#cancelled = cancelled;
  }

  /** 打断链长度（每张 [断点] +1）。停顿期间喂进来会实时延长最小停顿 */
  setNopeCount(count: number): void {
    if (this.#phase === 'fly' || this.#phase === 'hold') this.#nopeCount = count;
  }

  /**
   * **房主那个出牌窗口还开着吗。**
   *
   * `false` = 房主已经结算（生效或取消），该按要求飞弃牌堆 / 淡出了。
   * 停顿必须同时等它 —— 本地秒表算不准房主那边的时长，见 `update` 的 `hold`。
   */
  setWindowOpen(open: boolean): void {
    this.#windowOpen = open;
  }

  update(ticker: Ticker): void {
    if (this.#phase === 'done') return;
    this.#elapsed += ticker.deltaMS;

    switch (this.#phase) {
      case 'fly': {
        const t = clamp01(this.#elapsed / this.#phaseMs);
        this.#place(lerpPt(this.#from, this.#center, easeOut(t)), 1);
        if (t >= 1) this.#enter('hold');
        break;
      }
      case 'hold': {
        /**
         * 停在正中，直到**两个条件都满足**：
         *  ① 至少停满 `playHoldMs(nopeCount)`（基础 0.5s + 每张 [断点] 1s，上限 3s）；
         *  ② 房主那个窗口已经关了（`setWindowOpen(false)`）。
         *
         * ② 是同步的关键：房主还会多等宽限期，客户端不能自己
         * 卡着本地秒表就把牌飞走 —— 那样迟到的 [断点] 会让画面和状态对不上。
         */
        this.#place(this.#center, 1);
        if (this.#elapsed < playHoldMs(this.#nopeCount)) break;
        if (this.#windowOpen) break;
        this.#enter(playSettlePhase(this.#cancelled));
        break;
      }
      case 'glitch': {
        const t = clamp01(this.#elapsed / this.#phaseMs);
        // ★ 原地故障消失：停在正中，故障比例爬升、图案每帧换，最后快速没掉
        const g = this.#glitch;
        if (g !== null) g.setFrame(t, this.#elapsed / 1000);
        this.#place(this.#center, 1);
        this.view.alpha = t < 0.7 ? 1 : Math.max(0, 1 - (t - 0.7) / 0.3);
        if (t >= 1) this.#enter('done');
        break;
      }
      case 'fade': {
        const t = clamp01(this.#elapsed / this.#phaseMs);
        this.view.alpha = 1 - t;
        if (t >= 1) this.#enter('done');
        break;
      }
    }
  }

  destroy(): void {
    // ⚠️ 顺序和 DrawReveal 一样：先让翻牌层退场（它要把每帧登记摘掉），再销毁这一层
    this.#flip.destroy();
    this.#glitch?.destroy();
    this.#glitch = null;
    this.view.destroy({ children: true });
  }

  #enter(phase: PlayPhase): void {
    this.#phase = phase;
    this.#elapsed = 0;
    this.#phaseMs =
      phase === 'hold'
        ? playHoldMs(this.#nopeCount)
        : playPhaseMs(phase, this.#from, this.#center);
    // ★ 进入 glitch 相位才挂滤镜（放着不用的话每次出牌都要多跑一趟 pass）
    if (phase === 'glitch' && this.#glitch === null) {
      this.#glitch = new GlitchVanishFilter();
      this.view.filters = [this.#glitch];
    }
  }

  /** 摆到"牌心在 p、缩 scale" */
  #place(p: Pt, scale: number): void {
    this.view.position.set(p.x, p.y);
    this.view.scale.set(scale);
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function lerpPt(a: Pt, b: Pt, t: number): Pt {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}
